import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  lotDailyDataService,
  lotService,
  devicesService,
  sessionService,
} from "../utils/api";
import { cacheKeys } from "../utils/queryClient";

// Helper function to check if a date range is within another
const isDateRangeWithin = (
  innerStart: string,
  innerEnd: string,
  outerStart: string,
  outerEnd: string
) => {
  return innerStart >= outerStart && innerEnd <= outerEnd;
};

// Hook for fetching lot details
export const useLotDetails = (lotId: string) => {
  return useQuery({
    queryKey: cacheKeys.lot.detail(lotId),
    queryFn: async () => {
      console.log("🔄 Fetching lot details from API...");
      const response = await lotService.getLotById(lotId);
      console.log("✅ Lot details fetched successfully");
      return response;
    },
    enabled: !!lotId,
  });
};

interface AnalyticsData {
  date: string;
  [key: string]: any;
}

// Hook for fetching lot analytics
export const useLotAnalytics = (
  lotId: string,
  startDate: string,
  endDate: string,
  timeframe: "day" | "week" | "month" | "year"
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: cacheKeys.lot.analytics(lotId, startDate, endDate),
    queryFn: async () => {
      // Try to find cached data from larger time ranges
      const cachedQueries = queryClient.getQueryCache().findAll({
        queryKey: cacheKeys.lot.analytics(lotId, "", ""),
      });

      // Sort cached queries by date range size (largest first)
      const sortedCachedQueries = cachedQueries
        .filter((query) => {
          const [lotKey, idKey, analyticsKey, cachedStart, cachedEnd] =
            query.queryKey as string[];
          return isDateRangeWithin(startDate, endDate, cachedStart, cachedEnd);
        })
        .sort((a, b) => {
          const [lotKeyA, idKeyA, analyticsKeyA, aStart, aEnd] =
            a.queryKey as string[];
          const [lotKeyB, idKeyB, analyticsKeyB, bStart, bEnd] =
            b.queryKey as string[];
          const aRange = new Date(aEnd).getTime() - new Date(aStart).getTime();
          const bRange = new Date(bEnd).getTime() - new Date(bStart).getTime();
          return bRange - aRange; // Sort descending
        });

      // If we found cached data that contains our range, filter and return it
      if (sortedCachedQueries.length > 0) {
        const cachedData = sortedCachedQueries[0].state.data as AnalyticsData[];
        if (cachedData) {
          console.log("📦 Using cached data from larger time range");
          return cachedData.filter((entry) => {
            const entryDate = entry.date;
            return entryDate >= startDate && entryDate <= endDate;
          });
        }
      }

      // If no cached data found, fetch from API
      console.log("🔄 Fetching lot analytics from API...");
      const response = await lotDailyDataService.getDashboardMetrics(
        lotId,
        startDate,
        endDate
      );
      console.log("✅ Lot analytics fetched successfully");
      return response;
    },
    enabled: !!lotId && !!startDate && !!endDate,
  });
};

// Hook for fetching hourly data - ONLY for day view
export const useLotHourlyData = (
  lotId: string,
  date: string,
  timeframe: "day" | "week" | "month" | "year"
) => {
  return useQuery({
    queryKey: cacheKeys.lot.hourly(lotId, date),
    queryFn: async () => {
      console.log("🔄 Fetching hourly data from API...");
      const response = await lotDailyDataService.getLotHourlyByDate(
        lotId,
        date
      );
      console.log("✅ Hourly data fetched successfully");
      return response;
    },
    enabled: !!lotId && !!date && timeframe === "day", // Only fetch hourly data for day view
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Hook for fetching devices
export const useLotDevices = (lotId: string) => {
  return useQuery({
    queryKey: cacheKeys.lot.devices(lotId),
    queryFn: async () => {
      console.log("🔄 Fetching devices from API...");
      const response = await devicesService.getDevicesByLot(lotId);
      console.log("✅ Devices fetched successfully");
      return response;
    },
    enabled: !!lotId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Hook for fetching active sessions
export const useLotSessions = (lotId: string) => {
  return useQuery({
    queryKey: cacheKeys.lot.sessions(lotId),
    queryFn: async () => {
      console.log("🔄 Fetching active sessions from API...");
      const response = await sessionService.getSessionsByLot(lotId);
      console.log("✅ Active sessions fetched successfully");
      return response;
    },
    enabled: !!lotId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
